// Fil des gestes (« fil d'Ariane ») joint à un signalement (Task B1, vague 2 —
// observabilité). Tampon circulaire des 30 derniers évènements de l'écran :
// navigation, clics, soumissions, appels API, erreurs. Persisté dans
// sessionStorage (clé FIL_KEY) mais TOUJOURS lisible en mémoire même si le
// storage est absent ou hostile (mode privé, quota dépassé) — un signalement
// ne doit jamais échouer à cause du fil.
//
// Vie privée (ADR 0005, §6.13) : aucune valeur saisie dans un champ ne doit
// pouvoir transiter par un FilEvent. Les fonctions qui construisent des
// évènements à partir du DOM (describe-target.ts) ne lisent jamais `.value`.

export const FIL_KEY = "ecoute.fil";
export const FIL_MAX = 30;

export type FilEvent =
  | { t: string; kind: "page"; path: string }
  | { t: string; kind: "clic"; cible: string }
  | { t: string; kind: "soumission"; formulaire: string; champs: string[] }
  | {
      t: string;
      kind: "api";
      methode: string;
      chemin: string;
      statut: number | "reseau";
      dureeMs: number;
      // Nombre d'appels consécutifs identiques fusionnés dans cette entrée
      // (absent = 1). Une recherche qui tire 40 requêtes noyait les gestes.
      fois?: number;
    }
  | { t: string; kind: "erreur"; message: string };

// `Omit<Union, K>` du langage n'est PAS distributif (il ne garde que les
// propriétés communes à toutes les branches — bogue connu de TypeScript). On
// définit donc une variante distributive locale pour que `push` accepte
// chaque forme de FilEvent sans son `t`, propriété par propriété.
type SansHorodatage<T> = T extends unknown ? Omit<T, "t"> : never;

type FilStorage = Pick<Storage, "getItem" | "setItem">;

export interface Fil {
  push(e: SansHorodatage<FilEvent>): void;
  list(): FilEvent[];
  clear(): void;
}

function lireBuffer(storage: FilStorage | null): FilEvent[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(FIL_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FilEvent[]) : [];
  } catch {
    // storage hostile (jette) ou JSON corrompu → on repart d'un tampon vide,
    // jamais d'exception qui remonte à l'appelant.
    return [];
  }
}

export function createFil(storage: FilStorage | null, now: () => string): Fil {
  let buffer: FilEvent[] = lireBuffer(storage);

  function persister(): void {
    if (!storage) return;
    try {
      storage.setItem(FIL_KEY, JSON.stringify(buffer));
    } catch {
      // repli mémoire silencieux : le tampon en mémoire reste la source de
      // vérité pour cette session, seule la persistance échoue.
    }
  }

  return {
    push(e) {
      const evenement = { ...e, t: now() } as FilEvent;
      const dernier = buffer[buffer.length - 1];
      if (
        evenement.kind === "api" &&
        dernier?.kind === "api" &&
        dernier.methode === evenement.methode &&
        dernier.chemin === evenement.chemin &&
        dernier.statut === evenement.statut
      ) {
        // Même appel qu'à l'instant : on garde la date et la durée du plus
        // récent et on compte, au lieu de chasser les gestes du tampon.
        buffer = [...buffer.slice(0, -1), { ...evenement, fois: (dernier.fois ?? 1) + 1 }];
      } else {
        buffer = [...buffer, evenement].slice(-FIL_MAX);
      }
      persister();
    },
    list() {
      return [...buffer];
    },
    clear() {
      buffer = [];
      persister();
    },
  };
}

function resoudreUrl(input: RequestInfo | URL): string | null {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return null;
}

function methodeDeRequete(input: RequestInfo | URL): string | null {
  if (typeof Request !== "undefined" && input instanceof Request) return input.method;
  return null;
}

// Reconnaît un appel `fetch` vers l'API Supabase pour l'ajouter au fil.
// `null` pour tout ce qui n'est pas Supabase (autre origine, ou URL relative
// qu'on ne peut pas rattacher avec certitude) — on ne veut pas d'appels tiers
// dans le fil (ADR 0005).
export function apiEventFromFetch(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  supabaseUrl: string,
): { methode: string; chemin: string } | null {
  const brute = resoudreUrl(input);
  if (!brute) return null;

  let url: URL;
  let base: URL;
  try {
    url = new URL(brute);
    base = new URL(supabaseUrl);
  } catch {
    return null;
  }
  if (url.origin !== base.origin) return null;

  const methode = (init?.method ?? methodeDeRequete(input) ?? "GET").toUpperCase();
  return { methode, chemin: url.pathname };
}

function messageDErreur(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}

// Toujours une chaîne, jamais plus de `max` caractères — un signalement ne
// doit jamais gonfler à cause d'une erreur verbeuse (stack HTML, blob…).
export function truncateError(err: unknown, max = 300): string {
  const message = messageDErreur(err);
  return message.length > max ? message.slice(0, max) : message;
}
